/** Vakansiyaga driver yuborish oynasi: vakansiya sahifasi va o'ng tugma menyusi (Jobs, Carrier) uchun umumiy. */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useEligibleDrivers, useSubmitDriver } from "@/features/carriers/api";
import type { Job } from "@/features/carriers/types";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { Spinner } from "@/components/ui/misc";
import { toast } from "@/components/ui/toast";

export function SubmitDriverModal({ open, onOpenChange, job }: { open: boolean; onOpenChange: (o: boolean) => void; job: Job }) {
  const { t } = useTranslation("jobs");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const eligible = useEligibleDrivers(job.id, q, open);
  const rows = eligible.data?.results ?? [];
  const total = eligible.data?.total ?? 0;
  const submit = useSubmitDriver(job.id);

  // Qidiruv: yozishni to'xtatgandan 300 ms keyin so'raladi
  useEffect(() => {
    const t = window.setTimeout(() => setQ(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);
  useEffect(() => {
    if (open) {
      setSearch("");
      setQ("");
      setSelected(null);
      setNote("");
    }
  }, [open]);

  async function doSubmit() {
    if (!selected) return;
    const driver = rows.find((d) => d.id === selected);
    try {
      const res = await submit.mutateAsync({ driver: selected, version: driver?.version, note: note.trim() || undefined });
      if (res.warnings.length) toast.warning(t("submit.withWarnings", { name: res.driver.full_name }), res.warnings.join(" · "));
      else toast.success(t("submit.submitted", { name: res.driver.full_name, carrier: job.carrier_name }));
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.isConflict) toast.error(err.changedBy ? t("common:toast.conflict", { who: err.changedBy }) : t("common:toast.conflictAnon"));
        else toast.error(err.fields?.driver?.join(" ") ?? err.message);
      } else toast.error(t("submit.failed"));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("submit.title")}
      description={`${job.title} · ${job.carrier_name}`}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t("common:actions.cancel")}</Button>
          <Button variant="primary" loading={submit.isPending} disabled={!selected} onClick={doSubmit}>
            {t("common:actions.submit")}
          </Button>
        </>
      }
    >
      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("submit.searchPlaceholder")} autoFocus aria-label={t("submit.searchAria")} />
      <p className="mt-1 mb-2 text-[11px] text-muted">{t("submit.eligibleHint")}</p>
      <div className="max-h-[300px] space-y-1 overflow-y-auto" role="radiogroup">
        {eligible.isPending ? (
          <div className="py-4 text-center">
            <Spinner className="inline" />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-3 text-[13px] text-muted">{q ? t("submit.noMatch") : t("submit.noEligible")}</p>
        ) : (
          rows.map((d) => {
            const active = selected === d.id;
            return (
              <label key={d.id} className={cn("flex cursor-pointer items-start gap-3 rounded-[10px] border px-3 py-2", active ? "border-primary bg-primary-soft/40" : "border-line hover:bg-fill/60")}>
                <input type="radio" name="driver" className="mt-1 accent-primary" checked={active} onChange={() => setSelected(d.id)} />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-semibold">{d.full_name}</span>
                  <span className="block text-[11px] text-muted">
                    {[d.state, d.experience_label, t(`common:status.${d.status}`)].filter(Boolean).join(" · ")}
                  </span>
                  {d.warnings.length > 0 ? (
                    <span className="mt-0.5 block text-[11px] font-semibold text-warning">
                      {d.warnings.map((w) => `⚠ ${w}`).join(" · ")}
                    </span>
                  ) : (
                    <span className="mt-0.5 block text-[11px] text-success">{t("submit.matches")}</span>
                  )}
                </span>
              </label>
            );
          })
        )}
        {total > rows.length && <p className="py-2 text-center text-[11px] text-muted">{t("submit.moreHint", { shown: rows.length, total })}</p>}
      </div>
      <Textarea className="mt-3" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("submit.notePlaceholder")} rows={2} />
    </Modal>
  );
}
